/**
 * A cycle's ticket-pipeline summary, rendered as one chip per stage currently
 * holding a ticket (A7Y-3962 D1/D4/D5).
 *
 * Stage is fact-derived and settles independently of the session that wrote
 * it — a cycle's session finishes in seconds, its tickets land hours later,
 * written by OTHER sessions. So this never reads the session's outcome, only
 * `stages`, and elapsed time is computed here, client-side, from the
 * server's `enteredAt` — the server never formats time (D5).
 */
import type { AutobuildCycleStageSummary } from '@a7y/types';
import { CDID_TERMINAL_LIFECYCLE_STATES } from '@a7y/types';
import {
  fmtDuration,
  fmtLocal,
  LIFECYCLE_STATE_HINTS,
  LIFECYCLE_STATE_LABELS,
  LIFECYCLE_STATE_STYLES,
} from './shared';

export function StageChips({
  stages,
  now,
}: {
  stages: AutobuildCycleStageSummary[];
  now: number;
}) {
  if (stages.length === 0) {
    return <span className="text-zinc-400">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {stages.map((s) => {
        const label = LIFECYCLE_STATE_LABELS[s.stage];
        const className = LIFECYCLE_STATE_STYLES[s.stage];
        const title = `${LIFECYCLE_STATE_HINTS[s.stage]} — entered ${fmtLocal(s.enteredAt)}`;
        // `green` is deliberately NOT terminal (D5) — a green ticket is
        // waiting to land, exactly the "settles hours after the session
        // ends" case this whole column exists to show.
        const terminal = CDID_TERMINAL_LIFECYCLE_STATES.has(s.stage);

        if (terminal) {
          return (
            <span
              key={s.stage}
              className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${className}`}
              title={title}
            >
              {s.ticketCount} {label}
            </span>
          );
        }

        const elapsedSeconds = Math.max(0, (now - Date.parse(s.enteredAt)) / 1000);
        // The count prefix only earns its place once there is more than one
        // ticket to count — a lone ticket's own id is already on screen one
        // click away, so "1 Active" says nothing "Active" does not.
        const countPrefix = s.ticketCount > 1 ? `${s.ticketCount} ` : '';
        return (
          <span
            key={s.stage}
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${className}`}
            title={title}
          >
            {countPrefix}
            {label} · {fmtDuration(elapsedSeconds)}
          </span>
        );
      })}
    </div>
  );
}
